import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Box,
  Typography,
  Chip,
  IconButton,
} from "@mui/material";
import { ArrowBackIos, ArrowForwardIos } from "@mui/icons-material";
import dayjs from "dayjs";

const AttendanceCalendarDialog = ({
  calendarDialog,
  setCalendarDialog,
  selectedCalendar,
  batchDays = [],
  dayRange = [],
}) => {
  // 🧠 Attendance data map
  const statusMap = selectedCalendar.reduce((acc, day) => {
    acc[day.date] = day.status;
    return acc;
  }, {});

  const [viewMonth, setViewMonth] = useState(
    selectedCalendar.length ? dayjs(selectedCalendar[0].date) : dayjs()
  );

  // 📆 Calendar calculations
  const year = viewMonth.year();
  const month = viewMonth.month();
  const startOfMonth = dayjs(`${year}-${month + 1}-01`);
  const daysInMonth = startOfMonth.daysInMonth();
  const startDay = startOfMonth.day(); // Sunday = 0

  // 🎯 Batch range
  const startRange = dayRange?.[0] ? dayjs(dayRange[0]) : null;
  const endRange = dayRange?.[1] ? dayjs(dayRange[1]) : null;

  // 🔢 Prepare days (fill empty cells before first day)
  const calendarDays = [];
  for (let i = 0; i < startDay; i++) calendarDays.push(null);

  for (let i = 1; i <= daysInMonth; i++) {
    const date = dayjs(`${year}-${month + 1}-${i}`);
    const dateStr = date.format("YYYY-MM-DD");

    const inRange =
      (!startRange || date.isAfter(startRange.subtract(1, "day"))) &&
      (!endRange || date.isBefore(endRange.add(1, "day")));

    const weekday = date.format("dddd"); // Monday, Tuesday...
    const isBatchDay = batchDays?.includes(weekday);
    const status = statusMap[dateStr] || "not_marked";

    calendarDays.push({
      day: i,
      date: dateStr,
      status,
      inRange,
      isBatchDay,
    });
  }

  // 🎨 Color logic
  const getDayStyle = (day) => {
    if (!day.inRange) {
      return {
        backgroundColor: "#f0f0f0",
        color: "#9e9e9e",
      };
    }

    if (day.status === "present") {
      return { backgroundColor: "#4caf50", color: "#fff" };
    }
    if (day.status === "absent") {
      return { backgroundColor: "#f44336", color: "#fff" };
    }
     if (day.status === "holiday") {
      return { backgroundColor: "#1714e7ff", color: "#fff" };
    }

    if (day.isBatchDay) {
      // Batch active day but not marked yet
      return { backgroundColor: "#9e9e9e", color: "#fff" };
    }

    // Non-batch day within range
    return { backgroundColor: "#e0e0e0", color: "#666" };
  };

  // ⏪ / ⏩ month switch
  const handlePrevMonth = () => setViewMonth(viewMonth.subtract(1, "month"));
  const handleNextMonth = () => setViewMonth(viewMonth.add(1, "month"));

  // 🧮 Ensure proper 7xN grid (fill last row with blanks)
  const totalCells = Math.ceil(calendarDays.length / 7) * 7;
  while (calendarDays.length < totalCells) calendarDays.push(null);

  return (
    <Dialog
      open={calendarDialog}
      onClose={() => setCalendarDialog(false)}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ fontWeight: 600, textAlign: "center" }}>
        📅 Attendance Calendar
      </DialogTitle>

      <DialogContent>
        {/* 🔹 Month Navigation */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <IconButton onClick={handlePrevMonth}>
            <ArrowBackIos fontSize="small" />
          </IconButton>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {viewMonth.format("MMMM YYYY")}
          </Typography>
          <IconButton onClick={handleNextMonth}>
            <ArrowForwardIos fontSize="small" />
          </IconButton>
        </Box>

        {/* 🔹 Legend */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            gap: 2,
            mb: 2,
            flexWrap: "wrap",
          }}
        >
          <Chip label="Present" sx={{ backgroundColor: "#4caf50", color: "#fff" }} />
          <Chip label="Absent" sx={{ backgroundColor: "#f44336", color: "#fff" }} />
          <Chip label="Holiday" sx={{ backgroundColor: "#1714e7ff", color: "#fff" }} />
          <Chip label="Batch Active Day" sx={{ backgroundColor: "#9e9e9e", color: "#fff" }} />
          <Chip label="Outside Batch Range" sx={{ backgroundColor: "#f0f0f0", color: "#000" }} />
        </Box>

        {/* 🔹 Week Headers */}
        <Grid container spacing={1} justifyContent="center">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
            <Grid item xs={1.7} key={day}>
              <Typography
                sx={{
                  textAlign: "center",
                  fontWeight: 600,
                  color: "text.secondary",
                }}
              >
                {day}
              </Typography>
            </Grid>
          ))}
        </Grid>

        {/* 🔹 Calendar Grid */}
        <Grid container spacing={1} justifyContent="center" sx={{ mt: 1 }}>
          {calendarDays.map((day, index) => (
            <Grid item xs={1.7} key={index}>
              {day ? (
                <Box
                  sx={{
                    width: "100%",
                    aspectRatio: "1 / 1",
                    borderRadius: "10px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 600,
                    fontSize: "0.9rem",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                    transition: "transform 0.2s ease",
                    "&:hover": {
                      transform: day.inRange ? "scale(1.05)" : "none",
                    },
                    ...getDayStyle(day),
                  }}
                >
                  {day.day}
                </Box>
              ) : (
                <Box sx={{ width: "100%", aspectRatio: "1 / 1" }} />
              )}
            </Grid>
          ))}
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={() => setCalendarDialog(false)}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default AttendanceCalendarDialog;
